import {
  Select as MantineSelect,
  TextInput as MantineTextInput,
} from "@mantine/core";
import React, { ComponentPropsWithRef } from "react";
import styled from "styled-components";

import { focusOutline } from "~/styles/mixins";
import { blue, gray300, gray700 } from "~/styles/theme/color";
import { borderRadius } from "~/styles/theme/common";

/**
 * A drop-in replacement for MUI's `<MenuItem>`, used only as a data-carrying
 * marker inside `<StyledTextField select>`/`<StyledSelect>` children - it's
 * never actually mounted to the DOM, its props are read directly by the
 * parent instead.
 */
export interface MenuItemProps {
  value: string | number;
  children?: React.ReactNode;
  selected?: boolean;
  key?: React.Key;
}

export const MenuItem = (_props: MenuItemProps): null => null;

function menuItemsToData(children: React.ReactNode) {
  return React.Children.toArray(children)
    .filter((child): child is React.ReactElement<MenuItemProps> =>
      React.isValidElement(child)
    )
    .map((child) => ({
      value: String(child.props.value),
      label: String(child.props.children ?? ""),
    }));
}

const inputStyles = {
  label: { color: gray700 },
  // Mantine's Select marks its own internal input `readOnly` (it's a
  // pick-list, not free-typed), and Bootstrap's own `.form-control[readonly]`
  // rule (still applied via the `className="form-control"` convention at
  // call sites) styles readonly identically to disabled - greying out an
  // otherwise fully interactive dropdown. Force it back to white/normal.
  input: { borderColor: gray300, backgroundColor: "white", opacity: 1 },
};

/** Event shape a Formik `handleChange`/similar native-event-based handler expects */
type FakeChangeEvent = { target: { name?: string; value: string } };

export interface StyledSelectProps {
  name?: string;
  className?: string;
  label?: React.ReactNode;
  value?: string | number | null;
  defaultValue?: string | number | null;
  onChange?: (event: FakeChangeEvent) => void;
  error?: boolean;
  helperText?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  fullWidth?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  /** Accepted for API compatibility with MUI's TextField; Mantine has no variant concept */
  variant?: string;
}

export const StyledSelect = ({
  name,
  className,
  label,
  value,
  defaultValue,
  onChange,
  error,
  helperText,
  fullWidth,
  disabled,
  children,
}: StyledSelectProps) => (
  <MantineSelect
    name={name}
    label={label}
    // Mantine's useUncontrolled hook checks `value !== undefined` (not
    // `!= null`) to decide controlled vs uncontrolled - passing `null` here
    // when the caller only gave `defaultValue` would still count as
    // "controlled with value=null", permanently ignoring defaultValue and
    // any selection the user makes.
    value={value != null ? String(value) : undefined}
    defaultValue={defaultValue != null ? String(defaultValue) : undefined}
    onChange={(newValue) =>
      onChange?.({ target: { name, value: newValue ?? "" } })
    }
    error={error ? helperText : undefined}
    style={fullWidth ? { width: "100%" } : undefined}
    disabled={disabled}
    styles={inputStyles}
    // MUI's TextField applied `className` to the input box itself, not the
    // label+input wrapper - Mantine's bare `className` prop targets the
    // outer wrapper instead, which drew a Bootstrap `.form-control` border
    // around the label too. `classNames.input` targets just the input.
    classNames={className ? { input: className } : undefined}
    data={menuItemsToData(children)}
  />
);

export interface StyledTextFieldProps
  extends Omit<
    ComponentPropsWithRef<"input">,
    "value" | "defaultValue" | "onChange" | "size"
  > {
  select?: boolean;
  label?: React.ReactNode;
  value?: string | number | null;
  defaultValue?: string | number | null;
  onChange?: React.ChangeEventHandler<HTMLInputElement> &
    ((event: FakeChangeEvent) => void);
  error?: boolean;
  helperText?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  fullWidth?: boolean;
  children?: React.ReactNode;
  /** Accepted for API compatibility with MUI's TextField; Mantine has no variant concept */
  variant?: string;
}

export const StyledTextField = ({
  select,
  variant: _variant,
  value,
  defaultValue,
  onChange,
  error,
  helperText,
  fullWidth,
  children,
  className,
  ...rest
}: StyledTextFieldProps) => {
  if (select) {
    return (
      <StyledSelect
        {...rest}
        className={className}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
      >
        {children}
      </StyledSelect>
    );
  }
  return (
    <MantineTextInput
      {...rest}
      value={value ?? undefined}
      defaultValue={defaultValue ?? undefined}
      onChange={onChange}
      error={error ? helperText : undefined}
      style={fullWidth ? { width: "100%" } : undefined}
      styles={inputStyles}
      // MUI's TextField applied `className` to the input box itself, not
      // the label+input wrapper - Mantine's bare `className` prop targets
      // the outer wrapper instead, which drew a Bootstrap `.form-control`
      // border around the label too. `classNames.input` targets just the
      // input.
      classNames={className ? { input: className } : undefined}
    />
  );
};

export interface CheckboxProps extends ComponentPropsWithRef<"input"> {
  indeterminate?: boolean;
}

/** A checkbox component that allows declarative updates of the indeterminate property */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ indeterminate, ...rest }, forwardedRef) {
    const ref = React.useRef<HTMLInputElement | null>(null);

    // Synchronize the forwarded ref with our own internal ref
    React.useEffect(() => {
      if (typeof forwardedRef === "function") {
        forwardedRef(ref.current);
      } else if (forwardedRef) {
        forwardedRef.current = ref.current;
      }
    });

    // Imperatively update the indeterminate propery to sync with the prop
    React.useEffect(() => {
      if (ref.current) {
        ref.current.indeterminate = indeterminate ?? false;
      }
    }, [indeterminate]);

    return <input type="checkbox" ref={ref} {...rest} />;
  }
);

export const InputLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  color: ${gray700};
`;

export const TextInput = styled.input`
  ${focusOutline()};
  border-radius: ${borderRadius};
  border: 1px solid ${gray300};
  height: 2rem;
  flex: 1;
  padding: 1px 8px;

  &:focus {
    border: 1px solid ${blue};
  }

  ${InputLabel} > & {
    width: 100%;
  }
`;
